import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import {
  Button, Field, Input, MessageBar, MessageBarBody, Popover, PopoverSurface, PopoverTrigger, makeStyles, mergeClasses,
} from '@fluentui/react-components';
import { MAX_SAVED_FILTERS, SAVED_FILTER_NAME_MAX, type RosterFilterPreference, type SavedRosterFilter } from '@vigeo/shared';
import { PopupDialog, shellMetrics, tokens } from '@vigeo/ui';
import { placeWords } from '@/features/places';
import { COMPLIANCE_CHOICES, complianceLabel } from './RosterFilterControls.js';
import { CONFLICT_MESSAGE, LIMIT_MESSAGE, NAME_TAKEN_MESSAGE, preferencesCode, type RosterPreferences } from './rosterPreferences.js';
import { describePreference, preferenceIsOn, staleMessage, readStored, toPreference } from './savedFilterParts.js';
import { filterTags, type ComplianceChoice, type RosterFilters } from './rosterFilters.js';

/** A stored choice may no longer exist, and the label table would not know it: say the stored text then. */
const storedComplianceLabel = (choice: string): string => ((COMPLIANCE_CHOICES as readonly string[]).includes(choice) ? complianceLabel(choice as ComplianceChoice) : choice);

/**
 * The roster's "Saved filters" menu, the "Save these filters" step and the "from last time" strip
 * (Figma 977:14556: v2-2, v2-4, v2-5). Admin only: the page does not render any of this for a Viewer.
 *
 * Decisions made at build time (lead, 2026-10-05, under ADR-019): a stale part is left out and said so
 * (A); applying replaces the four saved parts and leaves the search box (B); saving needs one of the
 * four parts on (C); one inline message under the name, and at 20 the control is off with a line (D).
 */
const useStyles = makeStyles({
  surface: { width: '25rem', maxWidth: '92vw', padding: 0 },
  inner: { display: 'flex', flexDirection: 'column' },
  head: { padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalXS}`, fontWeight: tokens.fontWeightSemibold },
  /** The saved filters scroll inside the menu; the title and the Save step stay put (review 6). */
  list: { maxHeight: '45vh', overflowY: 'auto' },
  itemName: { fontWeight: tokens.fontWeightSemibold },
  item: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
  itemTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  apply: { minWidth: 0, justifyContent: 'flex-start', fontWeight: tokens.fontWeightSemibold, textAlign: 'start', paddingInline: 0 },
  sub: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  link: { color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold, minWidth: 'auto', paddingInline: tokens.spacingHorizontalXS },
  links: { display: 'flex', gap: tokens.spacingHorizontalXS, flexShrink: 0 },
  inline: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalS, backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium, padding: tokens.spacingHorizontalS },
  inlineBtns: { display: 'flex', gap: tokens.spacingHorizontalXS },
  rename: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  foot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, padding: tokens.spacingHorizontalM, borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  note: { padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  tags: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  tag: { display: 'inline-flex', paddingBlock: tokens.spacingVerticalXXS, paddingInline: tokens.spacingHorizontalS, backgroundColor: tokens.colorNeutralBackground2, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusSmall, fontSize: tokens.fontSizeBase300 },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  strip: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, backgroundColor: tokens.colorBrandBackground2, borderRadius: tokens.borderRadiusMedium, color: tokens.colorNeutralForeground1 },
  trigger: { minHeight: shellMetrics.controlHeight },
});

const nameProblem = (name: string): string | null => {
  const trimmed = name.trim();
  if (trimmed === '') return `Enter a name, 1 to ${SAVED_FILTER_NAME_MAX} characters.`;
  if (trimmed.length > SAVED_FILTER_NAME_MAX) return `A name is 1 to ${SAVED_FILTER_NAME_MAX} characters.`;
  return null;
};

/** The sentence under the name for a refused save. Never says what was typed. */
function saveFailure(error: unknown): { readonly message: string; readonly reload: boolean } {
  const code = preferencesCode(error);
  if (code === 'name-taken') return { message: NAME_TAKEN_MESSAGE, reload: false };
  if (code === 'limit') return { message: LIMIT_MESSAGE, reload: false };
  if (code === 'conflict') return { message: CONFLICT_MESSAGE, reload: true };
  if (code === 'area-not-in-use') return { message: 'A service area in these filters is not used by anyone on the roster now, so these filters cannot be saved.', reload: false };
  return { message: 'Your filters could not be saved. Try again.', reload: false };
}

/** "Your filters from last time are still on." Shown only while the four parts are what was applied on open (rule E). */
export function RememberedStrip() {
  const styles = useStyles();
  return (
    <div className={styles.strip} role="status">
      <span>Your filters from last time are still on. The search box starts empty; search words are not remembered.</span>
    </div>
  );
}

export interface SavedFiltersMenuProps {
  readonly preferences: RosterPreferences;
  readonly filters: RosterFilters;
  /** Whether somebody on the roster holds this service area now. */
  readonly areaInUse: (area: string) => boolean;
  /** Replace the four saved parts with this saved filter's; the page keeps the search box. */
  readonly onApply: (saved: SavedRosterFilter) => void;
  readonly onSaveThese: () => void;
}

/** One button in the filter row that opens the list. */
export function SavedFiltersMenu({ preferences, filters, areaInUse, onApply, onSaveThese }: SavedFiltersMenuProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [removing, setRemoving] = useState<string | 'unreadable' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const groupId = useId();
  const keepButton = useRef<HTMLButtonElement>(null);
  /** "Save these filters" closes this popover FIRST and opens the dialog after it has closed (PG12): never two pop-ups at once. */
  const saveNext = useRef(false);
  // The page passes a new function on every render; the effect below must not restart (and lose the pending open) when it does.
  const askToSave = useRef(onSaveThese);
  askToSave.current = onSaveThese;
  const view = preferences.view?.screens.roster;
  const saved = view?.saved ?? [];
  const hidden = view?.unreadableSaved ?? 0;
  const atLimit = saved.length >= MAX_SAVED_FILTERS;
  const canSave = preferenceIsOn(toPreference(filters));

  // When the popover is closed, the keyboard is back on the Saved filters button (cards 5 and 10 apply to popovers, PG11); if the
  // reason it closed was "Save these filters", the dialog then opens, and gives the keyboard back to that same button.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) { wasOpen.current = true; return; }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    trigger.current?.focus();
    if (!saveNext.current) return undefined;
    saveNext.current = false;
    // The popover animates out, so "closed" is not the moment its state flips but the moment it has left the page: the dialog opens then.
    let timer: ReturnType<typeof setTimeout>;
    let tries = 0;
    const openWhenGone = () => {
      tries += 1;
      if (document.getElementById(groupId) === null || tries > 40) { trigger.current?.focus(); askToSave.current(); return; }
      timer = setTimeout(openWhenGone, 25);
    };
    openWhenGone();
    return () => clearTimeout(timer);
  }, [open, groupId]);

  // A risky question (removing) starts the keyboard on Keep, so Enter removes nothing (PG8).
  useEffect(() => { if (removing !== null) keepButton.current?.focus(); }, [removing]);

  const run = async (action: () => Promise<unknown>, done: () => void) => {
    setBusy(true);
    setProblem(null);
    try {
      await action();
      done();
    } catch (error) {
      const code = preferencesCode(error);
      if (code === 'conflict' || code === 'no-such-filter') {
        setProblem(CONFLICT_MESSAGE);
        await preferences.reload();
      } else if (code === 'name-taken') {
        setProblem(NAME_TAKEN_MESSAGE);
      } else {
        setProblem('That could not be done. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(_, data) => { setOpen(data.open); if (!data.open) { setRenaming(null); setRemoving(null); setProblem(null); } }} positioning="below-end" trapFocus={false}>
      <PopoverTrigger disableButtonEnhancement>
        <Button ref={trigger} appearance="outline" className={styles.trigger}>Saved filters</Button>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <div id={groupId} className={styles.inner} role="group" aria-label="Saved filters">
          <div className={styles.head}>Saved filters · {saved.length} of {MAX_SAVED_FILTERS}</div>
          {problem === null ? null : (
            <MessageBar intent="warning"><MessageBarBody>{problem}</MessageBarBody></MessageBar>
          )}
          <div className={styles.list} role="region" aria-label="Your saved filters" tabIndex={0}>
          {saved.length === 0 && hidden === 0 ? <div className={styles.note}>No saved filters yet. Set some filters, then choose Save these filters.</div> : null}
          {saved.map((item) => {
            const readOut = readStored(item.filters, areaInUse);
            const stale = readOut.dropped.length > 0 ? staleMessage('saved', readOut.dropped, readOut.nothingLeft) : null;
            return (
              <div key={item.id} className={styles.item}>
                {renaming === item.id ? (
                  <div className={styles.rename}>
                    {/* The row stays recognisable while it is renamed: its current name and what it holds stay above the field. */}
                    <span className={styles.itemName}>{item.name}</span>
                    <span className={styles.sub}>{describePreference(item.filters, storedComplianceLabel)}</span>
                    <Field label="Rename" validationMessage={nameProblem(name) ?? undefined} validationState={nameProblem(name) === null ? 'none' : 'error'}>
                      <Input aria-label="Name" value={name} onChange={(_, data) => setName(data.value)} autoFocus />
                    </Field>
                    <div className={styles.inlineBtns}>
                      <Button onClick={() => { setRenaming(null); setProblem(null); }}>Cancel</Button>
                      <Button appearance="primary" disabled={busy || nameProblem(name) !== null} onClick={() => void run(() => preferences.rename(item.id, name.trim()), () => setRenaming(null))}>Save name</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.itemTop}>
                      <Button appearance="transparent" className={styles.apply} onClick={() => { onApply(item); setOpen(false); }} aria-label={`Use saved filter ${item.name}`}>{item.name}</Button>
                      <span className={styles.links}>
                        <Button appearance="transparent" size="small" className={styles.link} aria-label={`Rename ${item.name}`} onClick={() => { setName(item.name); setRenaming(item.id); setRemoving(null); setProblem(null); }}>Rename</Button>
                        <Button appearance="transparent" size="small" className={styles.link} aria-label={`Remove ${item.name}`} onClick={() => { setRemoving(item.id); setRenaming(null); setProblem(null); }}>Remove</Button>
                      </span>
                    </div>
                    <span className={styles.sub}>{describePreference(item.filters, storedComplianceLabel)}</span>
                    {stale === null ? null : <span className={mergeClasses(styles.sub)} role="note">{stale}</span>}
                  </>
                )}
                {removing === item.id ? (
                  <div className={styles.inline}>
                    <span>Remove this saved filter?</span>
                    <span className={styles.inlineBtns}>
                      <Button ref={keepButton} size="small" onClick={() => setRemoving(null)}>Keep</Button>
                      <Button size="small" appearance="primary" disabled={busy} onClick={() => void run(() => preferences.remove(item.id), () => setRemoving(null))}>Remove</Button>
                    </span>
                  </div>
                ) : null}
              </div>
            );
          })}
          {hidden > 0 ? (
            <div className={styles.item}>
              <span>{hidden === 1 ? '1 saved filter from another version can’t be shown.' : `${hidden} saved filters from another version can’t be shown.`}</span>
              {removing === 'unreadable' ? (
                <div className={styles.inline}>
                  <span>Remove {hidden === 1 ? 'it' : 'them'}?</span>
                  <span className={styles.inlineBtns}>
                    <Button ref={keepButton} size="small" onClick={() => setRemoving(null)}>Keep</Button>
                    <Button size="small" appearance="primary" disabled={busy} onClick={() => void run(() => preferences.removeUnreadable(), () => setRemoving(null))}>Remove</Button>
                  </span>
                </div>
              ) : (
                <Button appearance="transparent" size="small" className={styles.link} style={{ alignSelf: 'flex-start' }} onClick={() => { setRemoving('unreadable'); setRenaming(null); setProblem(null); }}>Remove {hidden === 1 ? 'it' : 'them'}</Button>
              )}
            </div>
          ) : null}
          </div>
          <div className={styles.foot}>
            <span>{atLimit ? LIMIT_MESSAGE : canSave ? '' : 'Turn on some filters to save them.'}</span>
            <Button appearance="primary" disabled={!canSave || atLimit} onClick={() => { saveNext.current = true; setOpen(false); }}>Save these filters</Button>
          </div>
        </div>
      </PopoverSurface>
    </Popover>
  );
}

export interface SaveFiltersDialogProps {
  readonly open: boolean;
  readonly preferences: RosterPreferences;
  /** The four parts that will be saved. The search text is not part of the type, so it cannot be here. */
  readonly parts: RosterFilterPreference;
  readonly onClose: () => void;
  /**
   * The button that opened it. Opened from the Saved filters popover, the menu puts the keyboard back on its own button before it
   * asks (a programmatic focus works in Safari on a Mac, a click does not), so this is only needed for another opener: the page
   * passes the "Save these filters" button of the Active filters strip when it wires one.
   */
  readonly opener?: RefObject<HTMLElement | null>;
}

/** Frame v2-5: a name (1 to 40) and a list of what will be saved. The dialog stays mounted and is driven by `open`. */
export function SaveFiltersDialog({ open, preferences, parts, onClose, opener }: SaveFiltersDialogProps) {
  const styles = useStyles();
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const typed = name !== '';
  const invalid = nameProblem(name);
  const tags = filterTags({ query: '', areas: parts.areas, compliance: parts.compliance as RosterFilters['compliance'], statuses: parts.statuses as RosterFilters['statuses'], phones: parts.phones as RosterFilters['phones'] }, complianceLabel, placeWords);

  const save = async () => {
    if (invalid !== null) return;
    setBusy(true);
    setProblem(null);
    try {
      await preferences.create({ name: name.trim(), filters: parts });
      setName('');
      onClose();
    } catch (error) {
      const failure = saveFailure(error);
      setProblem(failure.message);
      if (failure.reload) await preferences.reload();
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setName(''); setProblem(null); onClose(); };
  return (
    <PopupDialog
      open={open}
      title="Save these filters"
      size="medium"
      opener={opener}
      // Anything typed in the name is lost on every way of closing, so every way asks first (PG9). Saving is working: nothing closes it.
      dirty={typed}
      busy={busy}
      onClose={close}
      primary={{ label: 'Save filters', onClick: () => void save(), disabled: invalid !== null }}
    >
      <div className={styles.form}>
        <span>Give this set of filters a name. You can pick it again later from Saved filters.</span>
        <Field
          label="Name"
          required
          hint={`1 to ${SAVED_FILTER_NAME_MAX} characters.`}
          validationState={problem !== null || (typed && invalid !== null) ? 'error' : 'none'}
          validationMessage={problem ?? (typed ? invalid ?? undefined : undefined)}
        >
          <Input value={name} onChange={(_, data) => { setName(data.value); setProblem(null); }} placeholder="For example, Richmond, active" />
        </Field>
        <div>
          <strong>These filters will be saved</strong>
          <div className={styles.tags}>
            {tags.map((tag) => <span key={tag.key} className={styles.tag}>{tag.label}</span>)}
          </div>
        </div>
      </div>
    </PopupDialog>
  );
}
